import { useState, useEffect } from 'react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { useToast } from '@/components/ui/use-toast'
import {
  Trash2,
  Loader2,
  Check,
  X,
  Calendar,
} from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TeacherAvailabilityMatrix } from './TeacherAvailabilityMatrix'
import { useTranslation } from 'react-i18next'

interface Subject {
  id: string
  name: string
}

interface Teacher {
  id: string
  name: string
  subjects: string[]
  unavailableTimes?: Record<string, string[]>
}

interface TeachersConfigurationProps {
  teachers: Teacher[]
  subjects: Subject[]
  isLoading: boolean
  onAddTeacher: (teacher: Teacher) => void
  onDeleteTeacher: (teacherId: string) => void
  onUpdateTeacherAvailability?: (
    teacherId: string,
    unavailableTimes: Record<string, string[]>
  ) => void
  refetchTeachers?: () => Promise<any>
}

export function TeachersConfiguration({
  teachers,
  // subjects,
  isLoading,
  onAddTeacher,
  onDeleteTeacher,
  onUpdateTeacherAvailability,
  refetchTeachers,
}: TeachersConfigurationProps) {
  const { t } = useTranslation('timetable', { keyPrefix: 'timetable-folder.teachersConfiguration' })
  const { toast } = useToast()
  const [editingTeacher, setEditingTeacher] = useState<string | null>(null)
  const [editSubjects, setEditSubjects] = useState<string[]>([])
  const [selectedTeacher, setSelectedTeacher] = useState<Teacher | null>(null)
  const [isAvailabilityModalOpen, setIsAvailabilityModalOpen] = useState(false)

  // Check for invalid data and trigger refetch if needed
  useEffect(() => {
    // Only check after loading is complete
    if (!isLoading && refetchTeachers) {
      // Check if we have teachers but they're invalid
      const hasTeachers = teachers && teachers.length > 0
      const invalidTeachers = hasTeachers 
        ? teachers.filter(t => !t || !t.id || !t.name || t.name.trim() === '')
        : []
      
      // If we have invalid teachers (all teachers are invalid or some are invalid)
      if (invalidTeachers.length > 0) {
        console.warn('⚠️ TeachersConfiguration: Invalid data detected, refetching...', {
          invalidCount: invalidTeachers.length,
          totalCount: teachers?.length || 0
        })
        
        // Use refetch function with a small delay to avoid race conditions
        const timeoutId = setTimeout(() => {
          refetchTeachers().catch((error) => {
            console.error('Failed to refetch teachers:', error)
            // Fallback to reload if refetch fails after retry
            setTimeout(() => {
              if (refetchTeachers) {
                refetchTeachers().catch(() => {
                  window.location.reload()
                })
              } else {
                window.location.reload()
              }
            }, 2000)
          })
        }, 500)
        
        return () => clearTimeout(timeoutId)
      }
    }
  }, [teachers, isLoading, refetchTeachers])

  const handleEditSave = (teacherId: string) => {
    const updatedTeachers = teachers.map((teacher) =>
      teacher.id === teacherId
        ? { ...teacher, subjects: editSubjects }
        : teacher
    )

    // Update the teachers list in the parent component
    onDeleteTeacher(teacherId)
    const updatedTeacher = updatedTeachers.find((t) => t.id === teacherId)
    if (updatedTeacher) {
      onAddTeacher(updatedTeacher)
    }

    setEditingTeacher(null)

    toast({
      title: t('teacherUpdatedTitle'),
      description: t('teacherUpdatedDescription'),
    })
  }

  const handleEditCancel = () => {
    setEditingTeacher(null)
    setEditSubjects([])
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
        <CardDescription>
          {t('description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Separator />

        {isLoading ? (
          <div className="flex justify-center p-4">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span className="ml-2">{t('loading')}</span>
          </div>
        ) : teachers.length === 0 ? (
          <div className="text-center p-4 border rounded-md bg-muted/20">
            {t('empty')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('table.teacherName')}</TableHead>
                  {/* <TableHead>Subjects</TableHead> */}
                  <TableHead className="w-[150px]">{t('table.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {teachers
                  .filter((teacher) => teacher && teacher.id && teacher.name && teacher.name.trim() !== '')
                  .map((teacher) => (
                    <TableRow key={teacher.id}>
                      <TableCell>{teacher.name}</TableCell>
                     
                      <TableCell>
                        <div className="flex space-x-1">
                          {editingTeacher === teacher.id ? (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditSave(teacher.id)}
                                title={t('saveChanges')}
                                className="min-h-[44px] min-w-[44px]"
                              >
                                <Check className="h-4 w-4 text-green-500" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={handleEditCancel}
                                title={t('cancel')}
                                className="min-h-[44px] min-w-[44px]"
                              >
                                <X className="h-4 w-4 text-muted-foreground" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setSelectedTeacher(teacher)
                                  setIsAvailabilityModalOpen(true)
                                }}
                                title={t('manageAvailability')}
                                className="min-h-[44px] min-w-[44px]"
                              >
                                <Calendar className="h-4 w-4 text-green-500" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => onDeleteTeacher(teacher.id)}
                                title={t('deleteTeacher')}
                                className="min-h-[44px] min-w-[44px]"
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Teacher Availability Matrix */}
      {selectedTeacher && (
        <TeacherAvailabilityMatrix
          teacher={selectedTeacher}
          isOpen={isAvailabilityModalOpen}
          onClose={() => setIsAvailabilityModalOpen(false)}
          onSave={(teacherId, unavailableTimes) => {
            if (onUpdateTeacherAvailability) {
              onUpdateTeacherAvailability(teacherId, unavailableTimes)
            }
            setIsAvailabilityModalOpen(false)
            toast({
              title: t('availabilityUpdatedTitle'),
              description: t('availabilityUpdatedDescription', { name: selectedTeacher.name }),
            })
          }}
        />
      )}
    </Card>
  )
}
